import { Quaternion, Vector3 } from "three";

import { Dual } from "../../types/dual";
import {
  Basis,
  clampNumber,
  degToRad,
  quaternionAngle,
  quaternionsAlmostEqual,
} from "../../misc/util";

import { Edge } from "../edge";

const tempSwing = new Quaternion();
const tempTwist = new Quaternion();

const ra = new Vector3();

const pSwing = new Quaternion();
const pTwist = new Quaternion();
const pRa = new Vector3();
const pQ = new Quaternion();
const pBasis = new Basis();
const extentLen = 0.2;

const extSwing = new Quaternion();
const extTwist = new Quaternion();
const extRa = new Vector3();
const extQ = new Quaternion();

/**
 * Swing Twist constraint
 *
 * Decomposes `Edge` quaternion relative to parent into Swing and Twist:
 *
 * http://number-none.com/product/IK%20with%20Quaternion%20Joint%20Limits/
 *
 * And limits each independently. Swing is limited in a 4 segmented ellipse,
 * so you can have different limits for +/-x and +/-z.
 *
 * You can define this constraint abstractly and then `bind` it to a parent/child
 * `Edge` pair.
 */
export class GeneralConstraint {
  parent?: Edge;
  child?: Edge;

  saturated = false;

  // TODO: we will have 4 ellipses to allow for irregular limits
  private limits: Limits = {
    twist: new LinearLimit(),
    swing: new IrregularEllipticalLimit(),
  };

  private qLocal = new Quaternion();
  private qLocalInit = new Quaternion();
  private qLocalInitInv = new Quaternion();

  private relative = false;

  /**
   * Defines in degrees the allowable rotation around y (twist), x and z (swing) axes.
   *
   * @param twistLimits allowable delta rotation around y axis of each `Edge`
   * @param xLimits     allowable delta rotation around x axis of each `Edge`
   * @param zLimits     allowable delta rotation around z axis of each `Edge`
   * @param relative    if a constraint is relative, then when bound it calculates the initial
   *                    conditions relative to the parent `Edge`'s initial orientation. Otherwise
   *                    the initial conditions (and subsequent constraint enforcement) are in global
   *                    space
   */
  constructor(
    twistLimits?: [number, number],
    xLimits?: [number, number],
    zLimits?: [number, number],
    relative = false
  ) {
    this.limits.twist.set(twistLimits);
    this.limits.swing.set(xLimits, zLimits);

    this.relative = relative;
  }

  /**
   * Sets the child and parent edges and calculates the initial conditions
   * of the `Constraint` against which the limits are imposed.
   *
   * @param parent
   * @param child
   * @returns      success
   */
  bind(parent: Edge, child: Edge): boolean {
    // TODO: unconnected constraints don't currently work (at least across kabsch boundaries)
    // if (!parent.end.childEdges.includes(child))
    // {
    //   console.error("can't have relational constraint without direct linkage");
    //   return false;
    // }

    if (this.isBound()) {
      console.error("can't bind to bound constraint");
      return false;
    }

    this.parent = parent;
    this.child = child;

    if (this.relative) {
      // all constraints relative to initial swing and twist
      this.qLocalInit.multiplyQuaternions(this.parent.qInv, this.child.q);
      this.qLocalInitInv.copy(this.qLocalInit).invert();
    }

    this.qLocal.multiplyQuaternions(this.parent.qInv, this.child.q);

    return true;
  }

  /**
   * Allows user to change relative orientation of `Edge` pair then rebind (recalculate
   * initial conditions) so that limits are enforced based on orientation when rebind is
   * called
   *
   * @returns success
   */
  rebind(): boolean {
    if (!this.parent || !this.child) {
      console.error("can't rebind unbound constraint");
      return false;
    }
    const parent = this.parent;
    const child = this.child;

    this.parent = undefined;
    this.child = undefined;

    return this.bind(parent, child);
  }

  isBound(): boolean {
    return this.parent != undefined && this.child != undefined;
  }

  /**
   * Enforces orientational limits on `Edge` pair, can be enforces forwards (parent Edge
   * rotates to satisfy constraint) or backwards (child `Edge` rotates).
   *
   * @param backward which edge rotates to meet constraint
   * @param tempPos  allows user more control over whether to apply the update to the target
   *                 `Joint` or not, if `undefined`, then we directly set the child or parent
   *                 end or start (respectively) `Joint`
   * @param twistCol useful for implementing twist forces (not yet used) - the relative
   *                 twist is written to this if passed
   * @returns        success
   */
  apply(
    backward: boolean,
    tempPos?: Vector3,
    twistCol?: { angle: number }
  ): boolean {
    if (!this.parent || !this.child) {
      console.error("can't apply unbound constraint");
      return false;
    }

    // get local quaternion
    this.qLocal.multiplyQuaternions(this.parent.qInv, this.child.q);

    if (this.relative) {
      this.qLocal.premultiply(this.qLocalInitInv);
    }

    // decompose movable edge's quaternion relative to pair into
    // limited twist and swing
    if (this.limits.swing.isZero() && this.limits.twist.isZero()) {
      this.qLocal.set(0, 0, 0, 1);
    } else {
      swingTwistDecomposition(this.qLocal, tempSwing, tempTwist, this.limits);
      // reconstruct local quaternion
      tempSwing.multiply(tempTwist);
      if (quaternionsAlmostEqual(tempSwing, this.qLocal)) {
        this.saturated = false;
      } else {
        // console.log(`constraint ${this.parent?.name} saturated`, tempSwing, this.qLocal);
        this.saturated = true;
      }
      this.qLocal.copy(tempSwing);
    }

    if (this.relative) {
      this.qLocal.premultiply(this.qLocalInit);
    }

    // back to world space
    if (backward) {
      // backward means root to tip
      this.child.q.multiplyQuaternions(this.parent.q, this.qLocal);
      this.child.updateQuaternions();

      // update end joint position
      const toMove: Vector3 = tempPos ? tempPos : this.child.end.pos;
      toMove
        .copy(this.child.basis.right)
        .normalize()
        .multiplyScalar(this.child.edgeLen)
        .add(this.child.start.pos);

      if (twistCol) {
        // collect twist of child
        // TODO: this assumes that in applyConstraints in the backward pass
        //       we are accumulating angles for the same child
        swingTwistDecomposition(this.child.q, tempSwing, tempTwist);

        let angle = quaternionAngle(tempTwist);
        if (tempTwist.y < 0) {
          angle *= -1;
        }
        twistCol.angle += angle;
      }
    } else {
      this.parent.q.copy(this.qLocal).invert().premultiply(this.child.q);
      this.parent.updateQuaternions();

      // update start joint position
      const toMove: Vector3 = tempPos ? tempPos : this.parent.start.pos;
      toMove
        .copy(this.parent.basis.right)
        .negate()
        .normalize()
        .multiplyScalar(this.parent.edgeLen)
        .add(this.parent.end.pos);

      if (twistCol) {
        // collect twist of parent
        swingTwistDecomposition(this.parent.q, tempSwing, tempTwist);

        let angle = quaternionAngle(tempTwist);
        if (tempTwist.y < 0) {
          angle *= -1;
        }
        twistCol.angle += angle;
      }
    }

    return true;
  }

  /**
   * Resets the child `Edge` to the conditions when the `Constraint` was bound or
   * rebound.
   *
   * @returns success
   */
  reset(): boolean {
    if (!this.parent || !this.child) {
      return false;
    }

    this.child.q.multiplyQuaternions(this.parent.q, this.qLocalInit);
    this.child.updateQuaternions();

    const start = this.child.start;
    const end = this.child.end;
    end.pos
      .copy(this.child.basis.right)
      .normalize()
      .multiplyScalar(this.child.edgeLen)
      .add(start.pos);

    return true;
  }

  /**
   * Sets the `Edge` pair's swing orientation to some signed fraction of the maximum
   * (defined when constructed). Useful for annealing a FabrikChain.
   *
   * @param x normalised x rotation
   * @param z normalised z rotation
   * @returns success
   */
  setSwingExtent(x: number, z: number): boolean {
    if (!this.parent || !this.child) {
      return false;
    }

    let truncated = false;
    if (Math.abs(x) > 1) {
      x = x < 0 ? -1 : 1;
      truncated = true;
    }
    if (Math.abs(z) > 1) {
      z = z < 0 ? -1 : 1;
      truncated = true;
    }
    if (truncated) {
      console.warn("x and z must be normalised, truncating");
    }

    const limX = x * this.limits.swing.maxX;
    const limZ = z * this.limits.swing.maxZ;

    [extRa.x, extRa.z] = this.limits.swing.clamp(limX, limZ);

    setSwingTwist(extRa, extTwist, extSwing);

    extQ.multiplyQuaternions(extSwing, extTwist);

    if (this.relative) {
      extQ.premultiply(this.qLocalInit);
    }

    extQ.premultiply(this.parent.q);
    // extBasis.setFromQuaternion(pQ);

    this.child.q.copy(extQ);
    this.child.updateQuaternions();

    const start = this.child.start;
    const end = this.child.end;
    end.pos
      .copy(this.child.basis.right)
      .normalize()
      .multiplyScalar(this.child.edgeLen)
      .add(start.pos);

    return true;
  }

  /**
   * Used to populate a set of points that form a loop around the limits of the
   * Constraint. Useful for debugging / visualising the Constraint.
   *
   * @param points      empty array of points to be populated
   * @param quaternions ditto but quaternions (not used currently)
   * @returns           null
   */
  getSwingExtent(points?: Vector3[], quaternions?: Quaternion[]) {
    if (!this.parent || !this.child) {
      return;
    }

    const maxX = this.limits.swing.maxX;
    const maxZ = this.limits.swing.maxZ;

    const limits = [
      [0, maxZ],
      [maxX, maxZ],
      [maxX, 0],
      [maxX, -maxZ],
      [0, -maxZ],
      [-maxX, -maxZ],
      [-maxX, 0],
      [-maxX, maxZ],
    ];

    const subDivide = 4;

    pRa.y = 0;

    let start: Vector3 | undefined = undefined;

    for (let i = 0; i < limits.length; i++) {
      const limit = limits[i];
      const nLimit = limits[i + 1 == limits.length ? 0 : i + 1];

      const limDiff = [
        (nLimit[0] - limit[0]) / (subDivide + 1),
        (nLimit[1] - limit[1]) / (subDivide + 1),
      ];

      for (let s = 0; s < subDivide; s++) {
        [pRa.x, pRa.z] = this.limits.swing.clamp(limit[0], limit[1]);

        setSwingTwist(pRa, pTwist, pSwing);

        pQ.multiplyQuaternions(pSwing, pTwist);

        if (this.relative) {
          pQ.premultiply(this.qLocalInit);
        }

        pQ.premultiply(this.parent.q);
        pBasis.setFromQuaternion(pQ);

        if (quaternions) {
          quaternions.push(pQ.clone());
        }

        const point = new Vector3();

        point
          .copy(pBasis.right)
          .normalize()
          .multiplyScalar(extentLen)
          .add(this.child.start.pos);

        if (points) {
          if (!start) {
            start = point;
          } else {
            points.push(point);
          }

          points.push(point);
        }

        limit[0] += limDiff[0];
        limit[1] += limDiff[1];
      }
    }

    if (points && start) {
      points.push(start);
    }
  }
}

/**
 * Heart of `GeneralConstraint` class, but used elsewhere too (hence why we pulled it
 * out). Decomposes the relative quaternion into Swing and Twist quaternions, which
 * can be limited / measured independently.
 *
 * http://number-none.com/product/IK%20with%20Quaternion%20Joint%20Limits/
 *
 * @param q      input
 * @param swing  output
 * @param twist  output
 * @param limits swing (ellipse) and twist (linear) limits struct
 * @returns
 */
export function swingTwistDecomposition(
  q: Quaternion,
  swing: Quaternion,
  twist: Quaternion,
  limits?: Limits
): boolean {
  if (q.length() - 1 > 0.01) {
    // TODO: do we care about this enough to warn?
    // console.warn("impure rotation", q, q.length());
    q.normalize();
  }
  if (q.w < 0) {
    q.w = -q.w;
    q.x = -q.x;
    q.y = -q.y;
    q.z = -q.z;
  }

  const s = q.y * q.y + q.w * q.w;

  if (s < Number.EPSILON) {
    ra.x = q.x;
    ra.y = 0;
    ra.z = q.z;
  } else {
    const r = 1 / Math.sqrt(s);

    ra.y = q.y * r;

    // TODO: might be that the signs need flipping here
    // ra.x = (q.w * q.x - q.y * q.z) * r;
    // ra.z = (q.w * q.z + q.y * q.x) * r;
    ra.x = (q.w * q.x + q.y * q.z) * r;
    ra.z = (q.w * q.z - q.y * q.x) * r;
  }

  const x = ra.x;
  const z = ra.z;

  if (limits) {
    ra.y = limits.twist.clamp(ra.y);
    [ra.x, ra.z] = limits.swing.clamp(x, z);
  }

  const swingClamped = limits != undefined && (x != ra.x || z != ra.z);

  setSwingTwist(ra, twist, swing);

  return swingClamped;
}

function setSwingTwist(ra: Vector3, twist: Quaternion, swing: Quaternion) {
  twist.set(0, ra.y, 0, Math.sqrt(Math.max(0, 1 - ra.y * ra.y)));
  swing.set(
    ra.x,
    0,
    ra.z,
    Math.sqrt(Math.max(0, 1 - ra.x * ra.x - ra.z * ra.z))
  );
}

/**
 * Collector interface for Swing and Twist limits
 */
interface Limits {
  twist: LinearLimit;
  swing: IrregularEllipticalLimit;
}

/**
 * Composes 4 `EllipticalLimit`s to create a doubly assymetrical elliptical limits
 * (4 segments, with a maximum X and Z for each segment)
 */
class IrregularEllipticalLimit {
  maxX = 0;
  maxZ = 0;

  ellipses: EllipticalLimit[] = [
    new EllipticalLimit(),
    new EllipticalLimit(),
    new EllipticalLimit(),
    new EllipticalLimit(),
  ];

  /**
   * Called when Contraint is bound, sets the limits in degrees.
   *
   * @param xRangeDeg
   * @param zRangeDeg
   */
  set(xRangeDeg?: [number, number], zRangeDeg?: [number, number]) {
    this.ellipses[0].set(
      xRangeDeg ? Math.abs(xRangeDeg[0]) : undefined,
      zRangeDeg ? Math.abs(zRangeDeg[0]) : undefined
    );
    this.ellipses[1].set(
      xRangeDeg ? Math.abs(xRangeDeg[0]) : undefined,
      zRangeDeg ? Math.abs(zRangeDeg[1]) : undefined
    );
    this.ellipses[2].set(
      xRangeDeg ? Math.abs(xRangeDeg[1]) : undefined,
      zRangeDeg ? Math.abs(zRangeDeg[1]) : undefined
    );
    this.ellipses[3].set(
      xRangeDeg ? Math.abs(xRangeDeg[1]) : undefined,
      zRangeDeg ? Math.abs(zRangeDeg[0]) : undefined
    );

    if (!this.valid()) {
      console.warn("invalid irregular ellipse");
    }

    this.maxX = Math.max(this.ellipses[0].maxX, this.ellipses[2].maxX);
    this.maxZ = Math.max(this.ellipses[0].maxZ, this.ellipses[2].maxZ);
  }

  valid() {
    for (const el of this.ellipses) {
      if (!el.valid()) {
        return false;
      }
    }
    return true;
  }

  /**
   * @returns is the constraint rigid (in which case we can make optimisations in
   *          enforcement)
   */
  isZero(): boolean {
    return this.maxX == 0 && this.maxZ == 0;
  }

  /**
   * Offloads the clamping to the elliptical constraint depoending on which segment
   * the query lies within.
   *
   * @param x       x rotation to clamp (in normalised angular units (1 = 180 deg))
   * @param z       z rotation to clamp (in normalised angular units (1 = 180 deg))
   * @param maxIter
   * @returns       limited x and z relative orientations
   */
  clamp(x: number, z: number, maxIter = 50): [number, number] {
    // if maxX or maxZ are 0, this joint simplifies to fixed
    if (this.isZero()) {
      return [0, 0];
    }

    if (x < 0) {
      if (z < 0) {
        return this.ellipses[0].clamp(x, z, maxIter);
      } else {
        return this.ellipses[1].clamp(x, z, maxIter);
      }
    } else {
      if (z < 0) {
        return this.ellipses[3].clamp(x, z, maxIter);
      } else {
        return this.ellipses[2].clamp(x, z, maxIter);
      }
    }
  }
}

/**
 * Uses Dual numbers (https://github.com/dtecta/motion-toolkit/blob/master/moto/Dual.hpp)
 * to simplify Newton-Raphson optimisation of closest point on non-dimensional
 * ellipse.
 *
 */
class EllipticalLimit {
  maxX = 0;
  maxZ = 0;

  private maxXSq = 0;
  private maxZSq = 0;

  private p = new Dual();
  private result = new Dual();

  private tol = Number.EPSILON * 10;

  /**
   * Set limits in degrees
   *
   * @param maxXDeg
   * @param maxZDeg
   */
  set(maxXDeg?: number, maxZDeg?: number) {
    if (maxXDeg == null) {
      maxXDeg = 180;
    }
    if (maxZDeg == null) {
      maxZDeg = 180;
    }

    this.maxX = Math.sin(degToRad(maxXDeg) * 0.5);
    this.maxZ = Math.sin(degToRad(maxZDeg) * 0.5);

    this.maxXSq = this.maxX ** 2;
    this.maxZSq = this.maxZ ** 2;

    if (!this.valid()) {
      console.warn("invalid ellipse");
    }
  }

  /**
   * Checks that limits are valid
   */
  valid() {
    return 0 <= this.maxX && this.maxX <= 1 && 0 <= this.maxZ && this.maxZ <= 1;
  }

  /**
   * Tests if point lies within ellipse, if not, uses Newton-Raphson to find
   * closest point on ellipse. If limits are zero in one plane, degenerates
   * to a linear clamp.
   *
   * @param x       x rotation to clamp (in normalised angular units (1 = 180 deg))
   * @param z       z rotation to clamp (in normalised angular units (1 = 180 deg))
   * @param maxIter
   * @returns       limited x and z relative orientations
   */
  clamp(x: number, z: number, maxIter = 50): [number, number] {
    // if maxX or maxZ are 0, this joint simplifies to pivot, fixed or hinge
    if (this.maxX == 0 && this.maxZ == 0) {
      return [0, 0];
    } else if (this.maxX == 0) {
      const sign = z < 0 ? -1 : 1;
      return [0, sign * clampNumber(Math.abs(z), 0, this.maxZ)];
    } else if (this.maxZ == 0) {
      const sign = x < 0 ? -1 : 1;
      return [sign * clampNumber(Math.abs(x), 0, this.maxX), 0];
    } else if (this.distanceSq(x, z) <= 0) {
      // we're inside or on ellipse
      return [x, z];
    }

    // outside of ellipse so have to find closest point on ellipse to
    // [x, z]
    let t = 0;
    while (maxIter-- >= 0) {
      this.p.set(t, 1);
      // update this.result Dual
      this.genFunc(x, z);

      if (this.result.real <= this.tol) {
        break;
      }

      // newton-raphson step: t1 = t0 - F(t, x, y) / F'(t, x, y)
      t -= this.result.real / this.result.deriv;
    }

    return [
      (x * this.maxXSq) / (this.maxXSq + t),
      (z * this.maxZSq) / (this.maxZSq + t),
    ];
  }

  /**
   * Checks distance of point from ellipse boundary - negative means inside
   *
   * @param x
   * @param z
   * @returns distance^2 of point from boundary
   */
  private distanceSq(x: number, z: number): number {
    return (x / this.maxX) ** 2 + (z / this.maxZ) ** 2 - 1;
  }

  /**
   * Generator function for Dual number based implementation of Newton-Raphson
   *
   * @param x
   * @param z
   */
  private genFunc(x: number, z: number) {
    this.result
      .copy(this.p)
      .addScalar(this.maxXSq)
      .recipDivScalar(x * this.maxX)
      .square();
    this.p
      .addScalar(this.maxZSq)
      .recipDivScalar(z * this.maxZ)
      .square();

    this.result.add(this.p).addScalar(-1);
  }
}

/**
 * Simple linear Limit that clamps numbers to range
 */
class LinearLimit {
  private bottom = 0;
  private top = 0;

  set(rangeDeg?: [number, number]) {
    const rangeRad = rangeDeg ? rangeDeg.map(degToRad) : [-Math.PI, Math.PI];

    this.bottom = Math.sin(rangeRad[0] * 0.5);
    this.top = Math.sin(rangeRad[1] * 0.5);

    if (!this.valid()) {
      console.warn("invalid range");
    }
  }

  valid() {
    return (
      -1 <= this.bottom && this.bottom <= 1 && -1 <= this.top && this.top <= 1
    );
  }

  isZero(): boolean {
    return this.bottom == 0 && this.top == 0;
  }

  clamp(value: number): number {
    return clampNumber(value, this.bottom, this.top);
  }
}
